import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomModal from '../Common/CustomModal';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  archiveClientRequest,
  closeArchieveModal,
  resendClientInvitation,
} from '../../redux/client/clientSlice';
import { IClientRow } from './Columns';
import ClientArchiveModal from './ClientArchiveModal/ClientArchiveModal';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import { ClientType } from '../../types/client';

export enum OptionsEnum {
  RESEND,
  ASSIGN_ACTIVITIES,
  VIEW_PROGRESS,
  CHAT,
  ARCHIVE,
}

interface IRowActionsProps {
  row: IClientRow;
  hideOptions?: OptionsEnum[];
}

interface ICustomMenuItemProps {
  icon: string;
  text: string;
  onClick: () => void;
}

function CustomMenuItem({ icon, text, onClick }: ICustomMenuItemProps) {
  return (
    <MenuItem
      onClick={onClick}
      sx={{
        zIndex: 100,
        fontFamily: 'Satoshi',
        fontSize: 17,
        fontWeight: 400,
        lineHeight: '22px',
        letterSpacing: '-0.40799999237060547px',
        textAlign: 'left',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <img src={icon} alt="icon" style={{ marginRight: 8, width: 24, height: 24 }} />
        <Typography variant="body1">{text}</Typography>
      </Box>
    </MenuItem>
  );
}

export default function ClientDropdownMenu({ row, hideOptions }: IRowActionsProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const coach = useAppSelector((state) => state.coach.coach);
  const coachId = coach?.id || '';
  const archiveClientSuccess = useAppSelector((state) => state.client.archiveClientSuccess);
  const navigate = useNavigate();
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const { id } = useParams();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (value: string) => {
    if (value === 'archived') {
      setIsModalOpen(true);
      handleClose();
    } else {
      navigate(`/${value}`);
      handleClose();
    }
  };

  const handleResendClientInvitation = (value: string) => {
    dispatch(resendClientInvitation({ clientId: value }));
    handleClose();
  };

  const handleOnClick = (password?: string) => {
    dispatch(
      archiveClientRequest({
        clientId: row.id,
        coachId,
        email: row.email,
        password,
        clientGroupId: id,
      })
    );
  };

  const handleDismiss = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (archiveClientSuccess) {
      handleDismiss();
      dispatch(closeArchieveModal());
    }
  }, [archiveClientSuccess]);

  const MenuOptions: React.Element[] = [];

  if (
    !hideOptions?.includes(OptionsEnum.RESEND) &&
    checkPermissions([UserPermissionType.CORE_MODULE_ADD_NEW_CLIENT], true) &&
    row.clientType === ClientType.INDIVIDUAL
  ) {
    MenuOptions.push(
      <CustomMenuItem
        icon="/assets/icons/invitation.svg"
        text={t('clientTable.actions.resendInvitation')}
        onClick={() => handleResendClientInvitation(row.id)}
      />
    );
  }
  if (!hideOptions?.includes(OptionsEnum.ASSIGN_ACTIVITIES)) {
    MenuOptions.push(
      <CustomMenuItem
        icon="/assets/icons/assign-activity.svg"
        text={t('clientTable.actions.assignActivities')}
        onClick={() => handleMenuItemClick(`clients/${row.id}?tab=activities`)}
      />
    );
  }
  if (!hideOptions?.includes(OptionsEnum.VIEW_PROGRESS)) {
    MenuOptions.push(
      <CustomMenuItem
        icon="/assets/icons/view-progress.svg"
        text={t('clientTable.actions.viewProgress')}
        onClick={() => handleMenuItemClick(`clients/${row.id}?tab=progress`)}
      />
    );
  }
  if (!hideOptions?.includes(OptionsEnum.CHAT) && row.clientType === ClientType.INDIVIDUAL) {
    MenuOptions.push(
      <CustomMenuItem
        icon="/assets/icons/chat-dropdown.svg"
        text={t('common.modules.chat')}
        onClick={() => handleMenuItemClick('chat')}
      />
    );
  }

  if (!hideOptions?.includes(OptionsEnum.ARCHIVE)) {
    if (row.clientType === ClientType.GROUP) {
      if (checkPermissions([UserPermissionType.CLIENT_GROUP_MODULE_DELETE_CLIENT_GROUP], true)) {
        MenuOptions.push(
          <CustomMenuItem
            icon="/assets/icons/archive-red.svg"
            text={t('clientTable.archiveClient.btnText')}
            onClick={() => handleMenuItemClick('archived')}
          />
        );
      } else if (checkPermissions([UserPermissionType.CORE_MODULE_ARCHIVE_CLIENTS])) {
        MenuOptions.push(
          <CustomMenuItem
            icon="/assets/icons/archive-red.svg"
            text={t('clientTable.archiveClient.btnText')}
            onClick={() => handleMenuItemClick('archived')}
          />
        );
      }
    } else if (
      row.clientType !== ClientType.GROUP &&
      checkPermissions([UserPermissionType.CORE_MODULE_ARCHIVE_CLIENTS])
    ) {
      MenuOptions.push(
        <CustomMenuItem
          icon="/assets/icons/archive-red.svg"
          text={t('clientTable.archiveClient.btnText')}
          onClick={() => handleMenuItemClick('archived')}
        />
      );
    }
  }

  return (
    <Box>
      <IconButton onClick={handleClick}>
        <MoreHorizIcon />
      </IconButton>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {MenuOptions}
      </Menu>
      {isModalOpen && (
        <CustomModal isOpen={isModalOpen} width={450}>
          <ClientArchiveModal
            handleOnClick={handleOnClick}
            handleDismiss={handleDismiss}
            firstName={row.firstName}
            lastName={row.lastName}
            email={row.email}
            profilePicture={row.profilePic}
            icon="/assets/icons/archive-down.svg"
            title={t('clientTable.archiveClient.title')}
            subTitle={t('clientTable.archiveClient.subtitle')}
            btnText={t('clientTable.archiveClient.btnText')}
          />
        </CustomModal>
      )}
    </Box>
  );
}
